import { decode as decodePolyline } from '@googlemaps/polyline-codec';
import React, { useState } from 'react';
import { Marker, Polyline, Tooltip } from 'react-leaflet';
import {
  clampLatLngToPolygon,
  resolveZoneCenter,
  resolveZonePolygon,
} from 'features/Responder/features/Tours/features/TourBuilder/utils/zoneRouteMath';
import { CircleShape, CrosslineShape, PolygonShape } from './Geometry';

/**
 * Render checkpoint shapes, anchor markers, and optional tails.
 * @param {object} props
 * @returns {JSX.Element|null}
 */
const CheckpointsLayer = ({
  checkpoints,
  selectedCheckpointId,
  setSelectedCheckpointId,
  handleIcon,
  checkpointIcon,
  metersPerDegreeLat,
  metersPerDegreeLng,
  snapToRoad,
  computeEtaFor,
  updateCheckpoint,
  suppressFocusNext,
  legStartsMeters,
  editable = true,
  showZoneAnchors = true,
}) => {
  const [draggingPositions, setDraggingPositions] = useState({});

  // Track in-progress drag positions to keep UI responsive.
  const setPendingPosition = (id, latlng) => {
    setDraggingPositions((prev) => {
      if (!latlng) {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return {
        ...prev,
        [id]: { lat: latlng.lat, lng: latlng.lng },
      };
    });
  };

  /**
   * Attach drag handlers with optional clamping.
   * @param {object} handlers
   * @param {string|number} checkpointId
   * @param {(pos: {lat: number, lng: number}) => {lat: number, lng: number}|null} clampFn
   */
  const attachDragHandlers = (handlers, checkpointId, clampFn) => {
    handlers.dragstart = (e) => {
      const map = e?.target?._map;
      map?.dragging?.disable?.();
    };
    handlers.drag = (e) => {
      suppressFocusNext.current = true;
      const newPos = e.target.getLatLng();
      const nextPos = clampFn ? clampFn(newPos) : newPos;
      if (
        clampFn &&
        nextPos &&
        (Number(nextPos.lat) !== Number(newPos.lat) || Number(nextPos.lng) !== Number(newPos.lng))
      ) {
        e.target.setLatLng(nextPos);
      }
      setPendingPosition(checkpointId, nextPos);
    };
    handlers.dragend = async (e) => {
      suppressFocusNext.current = true;
      const newPos = e.target.getLatLng();
      const clamped = clampFn ? clampFn(newPos) : newPos;
      const snapped = await snapToRoad(clamped);
      const finalPos = clampFn ? clampFn(snapped) : snapped;
      const { stopIndex, etaSeconds } = computeEtaFor(finalPos, legStartsMeters);
      updateCheckpoint(checkpointId, {
        lat: finalPos.lat,
        lng: finalPos.lng,
        stopIndex,
        etaSeconds,
      });
      setPendingPosition(checkpointId, null);
      const map = e?.target?._map;
      map?.dragging?.enable?.();
    };
  };

  const renderCheckpoint = (cp) => {
    const geometryType = (cp?.geometry?.type || 'circle').toLowerCase();
    const zoneId = cp?.zoneId ?? cp?.geometry?.zoneId;
    const isZone = geometryType === 'zone' || cp?.geometry?.isZone || zoneId != null;
    const editing = editable && selectedCheckpointId === cp.id && !isZone;
    const zonePolygon = isZone ? resolveZonePolygon(cp) : null;
    const clampZone =
      Array.isArray(zonePolygon) && zonePolygon.length >= 3
        ? (pos) => clampLatLngToPolygon(pos, zonePolygon)
        : null;

    const baseLat = Number(cp.lat);
    const baseLng = Number(cp.lng);
    const lat = draggingPositions[cp.id]?.lat ?? (Number.isFinite(baseLat) ? baseLat : 0);
    const lng = draggingPositions[cp.id]?.lng ?? (Number.isFinite(baseLng) ? baseLng : 0);
    const centerLatLng = [lat, lng];
    const zoneCenter = isZone ? resolveZoneCenter(cp) : null;
    const zoneLat = draggingPositions[cp.id]?.lat ?? zoneCenter?.lat;
    const zoneLng = draggingPositions[cp.id]?.lng ?? zoneCenter?.lng;
    const zoneCenterLatLng =
      Number.isFinite(zoneLat) && Number.isFinite(zoneLng) ? [zoneLat, zoneLng] : null;
    const markerHandlers = {
      click: () => {
        suppressFocusNext.current = true;
        setSelectedCheckpointId(cp.id);
      },
    };
    if (editable && !isZone) {
      attachDragHandlers(markerHandlers, cp.id);
    }
    const zoneMarkerHandlers = {
      click: () => {
        suppressFocusNext.current = true;
        setSelectedCheckpointId(cp.id);
      },
    };
    if (editable && isZone) {
      attachDragHandlers(zoneMarkerHandlers, cp.id, clampZone);
    }

    return (
      <React.Fragment key={`cp-${cp.id}`}>
        {geometryType === 'circle' && !isZone && (
          <CircleShape
            cp={cp}
            setSelectedCheckpointId={setSelectedCheckpointId}
            handleIcon={handleIcon}
            metersPerDegreeLat={metersPerDegreeLat}
            metersPerDegreeLng={metersPerDegreeLng}
            updateCheckpoint={updateCheckpoint}
            suppressFocusNext={suppressFocusNext}
            editing={editing}
          />
        )}

        {geometryType === 'crossline' && !isZone && (
          <CrosslineShape
            cp={cp}
            setSelectedCheckpointId={setSelectedCheckpointId}
            handleIcon={handleIcon}
            metersPerDegreeLat={metersPerDegreeLat}
            metersPerDegreeLng={metersPerDegreeLng}
            updateCheckpoint={updateCheckpoint}
            suppressFocusNext={suppressFocusNext}
            editing={editing}
          />
        )}

        {(geometryType === 'polygon' || isZone) && (
          <PolygonShape
            cp={cp}
            setSelectedCheckpointId={setSelectedCheckpointId}
            updateCheckpoint={updateCheckpoint}
            suppressFocusNext={suppressFocusNext}
            handleIcon={handleIcon}
            metersPerDegreeLat={metersPerDegreeLat}
            metersPerDegreeLng={metersPerDegreeLng}
            editing={editing}
            isZone={isZone}
          />
        )}

        {/* Render additionalPolyline if present as encoded polyline */}
        {typeof cp.additionalEncodedPolyline === 'string' && cp.additionalEncodedPolyline && (
          <Polyline
            positions={decodePolyline(cp.additionalEncodedPolyline)}
            pathOptions={{ color: 'blue', weight: 3, dashArray: '6 12' }}
          />
        )}

        {isZone && showZoneAnchors && zoneCenterLatLng && (
          <Marker
            position={zoneCenterLatLng}
            draggable={editable}
            bubblingMouseEvents={false}
            icon={checkpointIcon}
            eventHandlers={zoneMarkerHandlers}
          >
            <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
              Zone anchor
            </Tooltip>
          </Marker>
        )}
        {!isZone && (
          <Marker
            position={centerLatLng}
            draggable={editable}
            bubblingMouseEvents={false}
            icon={checkpointIcon}
            eventHandlers={markerHandlers}
          >
            <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
              Checkpoint
            </Tooltip>
          </Marker>
        )}
      </React.Fragment>
    );
  };

  return Array.isArray(checkpoints) ? checkpoints.map((cp) => renderCheckpoint(cp)) : null;
};

export default CheckpointsLayer;
